import { Checkbox } from "@/common/ui/checkbox";
import { Label } from "@/common/ui/label";
import { cn } from "@/lib/utils";

import type { Permission } from "../types";

interface PermissionCheckboxGroupProps {
  permissions: readonly Permission[];
  selectedIds: string[];
  onToggle: (permissionId: string) => void;
  disabled?: boolean;
}

const DOMAIN_LABELS: ReadonlyArray<{ prefix: string; label: string }> = [
  { prefix: "auth", label: "Authentication" },
  { prefix: "members", label: "Members" },
  { prefix: "org", label: "Organization" },
  { prefix: "identities", label: "Identities" },
  { prefix: "support", label: "Support" },
  { prefix: "privacy", label: "Privacy" },
  { prefix: "classes", label: "Classes" },
  { prefix: "workouts", label: "Workouts" },
  { prefix: "schedules", label: "Schedules" },
];

function domainOf(key: string): string {
  const idx = key.indexOf(":");
  return idx === -1 ? key : key.slice(0, idx);
}

export function PermissionCheckboxGroup({
  permissions,
  selectedIds,
  onToggle,
  disabled = false,
}: PermissionCheckboxGroupProps): React.ReactElement {
  const byDomain = new Map<string, Permission[]>();
  for (const p of permissions) {
    const d = domainOf(p.key);
    const list = byDomain.get(d) ?? [];
    list.push(p);
    byDomain.set(d, list);
  }

  const orderedDomains = [
    ...DOMAIN_LABELS.filter((d) => byDomain.has(d.prefix)),
    ...Array.from(byDomain.keys())
      .filter((d) => !DOMAIN_LABELS.some((dl) => dl.prefix === d))
      .map((d) => ({ prefix: d, label: d })),
  ];

  return (
    <div className="space-y-4" data-testid="permission-checkbox-group">
      {orderedDomains.map(({ prefix, label }) => {
        const perms = byDomain.get(prefix) ?? [];
        return (
          <div key={prefix} className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {label}
            </Label>
            <div className="space-y-1">
              {perms.map((perm) => {
                const checked = selectedIds.includes(perm.id);
                return (
                  <label
                    key={perm.id}
                    htmlFor={`perm-${perm.id}`}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-md border p-2 text-sm transition-colors",
                      checked ? "border-primary bg-primary/5" : "hover:bg-accent",
                      disabled && "cursor-not-allowed opacity-60"
                    )}
                  >
                    <Checkbox
                      id={`perm-${perm.id}`}
                      data-testid={`perm-checkbox-${perm.key}`}
                      checked={checked}
                      onCheckedChange={() => {
                        if (!disabled) onToggle(perm.id);
                      }}
                      disabled={disabled}
                      className="mt-0.5"
                      aria-label={perm.key}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{perm.key}</p>
                      {perm.description && (
                        <p className="text-xs text-muted-foreground">{perm.description}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
